import React, { useId } from 'react';
import { twJoin } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { ImageFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { getOptionTileStateClasses } from '@/ui/components/shared/option-tile/option-tile-styles';
import { CrystallizeContentHelper } from '@/utils/crystallize-content-helper';

export interface DoorKnobOptionProps {
  image?: ImageFragment | null;
  isSelected: boolean;
  onSelect: () => void;
}

export function DoorKnobOption({ image, isSelected, onSelect }: Readonly<DoorKnobOptionProps>) {
  const t = useTranslations('DoorKnobOption');
  const captionId = useId();
  // The yes and no tiles share the generic label, so the caption names each one when it has text.
  const hasCaption = !CrystallizeContentHelper.isRichTextEmpty(image?.caption);

  return (
    <div className="w-1/2 items-center justify-center">
      <button
        type="button"
        className={twJoin(
          'flex w-full items-center justify-center rounded-md p-4',
          getOptionTileStateClasses(isSelected),
        )}
        onClick={onSelect}
        aria-label={t('Select door knob')}
        aria-labelledby={hasCaption ? captionId : undefined}
        aria-pressed={isSelected}
      >
        <div>
          <OptimizedImage
            image={image}
            sizes="100px"
            width={100}
            height={100}
            className="mb-2 h-12"
          />
        </div>
      </button>
      {/* RichTextRenderer takes no id, so this unstyled wrapper is the label target. */}
      <div id={captionId}>
        <RichTextRenderer className="mt-1 text-center font-bold" blocks={image?.caption} />
      </div>
    </div>
  );
}
